import React from "react";
import { useQuery } from "@tanstack/react-query";
import { api, errMsg } from "@/lib/api";
import { useT } from "@/context/I18nContext";
import PageHeader from "@/components/common/PageHeader";
import EmptyState, { LoadingCard, ErrorState } from "@/components/common/EmptyState";
import { MessageSquare, Flag } from "lucide-react";

function List({ title, testId, url, icon }) {
  const t = useT();
  const q = useQuery({
    queryKey: [testId],
    queryFn: async () => (await api.get(url)).data,
    retry: false,
  });
  const items = Array.isArray(q.data) ? q.data : q.data?.items || q.data?.conversations || q.data?.flagged || [];
  return (
    <div>
      <div className="mb-4">
        <h2 className="font-heading text-2xl text-ink-900">{title}</h2>
      </div>
      {q.isLoading && <LoadingCard height="h-32" />}
      {q.isError && <ErrorState message={errMsg(q.error)} onRetry={() => q.refetch()} />}
      {!q.isLoading && !q.isError && items.length === 0 && (
        <EmptyState icon={icon} title={t("common.empty")} testId={`${testId}-empty`} />
      )}
      <div className="space-y-2" data-testid={testId}>
        {items.map((it, i) => (
          <div key={it.id || i} className="rounded-2xl bg-white shadow-card p-4">
            <div className="text-xs text-ink-500">{it.user_email || it.user || it.name || "—"}</div>
            <div className="text-sm text-ink-900 mt-1 whitespace-pre-wrap line-clamp-3">
              {it.text || it.message || it.content || it.snippet || it.reason || JSON.stringify(it).slice(0, 200)}
            </div>
            {(it.created_at || it.updated_at) && (
              <div className="text-xs text-ink-500 mt-2">{new Date(it.created_at || it.updated_at).toLocaleString()}</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export function AdminConversations() {
  const t = useT();
  return (
    <div className="space-y-6">
      <PageHeader eyebrow={t("nav.admin")} title={t("nav.conversations")} subtitle={t("admin.subtitle")} />
      <List title={t("nav.conversations")} testId="admin-conversations" url="/admin/conversations" icon={MessageSquare} />
    </div>
  );
}

export function AdminFlagged() {
  const t = useT();
  return (
    <div className="space-y-6">
      <PageHeader eyebrow={t("nav.admin")} title={t("nav.flagged")} subtitle={t("admin.subtitle")} />
      <List title={t("nav.flagged")} testId="admin-flagged" url="/admin/flagged" icon={Flag} />
    </div>
  );
}

export default AdminConversations;
